import { Component, OnDestroy, OnInit } from '@angular/core';
import { PdfViewer } from '../core/pdfViewer.service';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import brandInfo from "src/environments/brand.json";
import {
  getContractByUuid,
  updatePDFExpiration,
  updatePDFExpirationSuccess,
  updatePDFExpirationFailed
} from '../_actions/pdf.actions';
import { AppConfigService } from '../appconfig/appconfig.service';
import { DomSanitizer } from '@angular/platform-browser';
import { showInlineLoader } from '../_actions/inline-loader.actions';
import { DataLookupService } from '../_services/data-lookup.service';
import { ViewContractRequest } from '../_models/pdf';


@Component({
  selector: 'app-view-contract',
  standalone: false,
  templateUrl: './view-contract.component.html',
  styleUrls: ['./view-contract.component.scss']
})
export class ViewContractComponent implements OnInit, OnDestroy {
  pdf$: Observable<any>;
  pdfSub: Subscription;
  updateExpirationSub: Subscription;
  updateExpirationFailedSub: Subscription;
  src: any;
  pdf: any;
  branding: any;
  isLoading: boolean;
  linkClicked = false;
  newWelcomeFlow: boolean;
  emailLinkLoading = false;
  emailLinkError: string | null = null;
  orderLineItemId: string | null = null;

  constructor(
    private pdfViewer: PdfViewer,
    private store: Store<{ pdf: any }>,
    private route: ActivatedRoute,
    private router: Router,
    private appConfig: AppConfigService,
    private sanitizer: DomSanitizer,
    private actions$: Actions,
    private dataLookup: DataLookupService
  ) { }

  ngOnInit(): void {
    this.isLoading = true;
    this.newWelcomeFlow = brandInfo.newWelcomeFlow;
    this.store.dispatch(showInlineLoader());
    const uuid = this.route.snapshot.params['uuid'];
    const randomString = this.route.snapshot.params['randomString'];
    const params = this.route.snapshot.queryParams;
    const contactToken = params['contactToken'];
    const isRenewalLink = randomString && /^[0-9a-fA-F]{64}$/.test(randomString);

    // New contract-link-renewal flow: randomString param is the renewal token
    if (isRenewalLink) {
      this.dataLookup.verifyContractLinkRenewal(randomString).subscribe({
        next: (res: any) => {
          if (res && res.status === 'OK' && res.data && res.data.pdfRaw) {
            this.pdf = res;
            this.isLoading = false;
            this.src = this.sanitizer.bypassSecurityTrustResourceUrl(
              'data:application/pdf;base64,' + res.data.pdfRaw
            );
          } else {
            const req: ViewContractRequest = {
              brandName: brandInfo.brand,
              uuid,
              viewType: params['type'] ?? null
            };
            this.store.dispatch(getContractByUuid(req));
          }
        },
        error: () => {
          const req: ViewContractRequest = {
            brandName: brandInfo.brand,
            uuid,
            viewType: params['type'] ?? null
          };
          this.store.dispatch(getContractByUuid(req));
        }
      });
    }

    // Normal UUID flow - only process if not a renewal link
    if (!isRenewalLink) {
      this.route.queryParams.subscribe((qParams) => {
        if (qParams['contactToken']) return;
        const request: any = {
          brandName: brandInfo.brand,
          uuid,
          viewType: qParams['type'] ? qParams['type'] : null
        };
        this.store.dispatch(getContractByUuid(request));
      });
    }

    this.appConfig.getSettings().subscribe((result) => {
      this.branding = result;
    });

    this.updateExpirationSub = this.actions$
      .pipe(ofType(updatePDFExpirationSuccess))
      .subscribe(() => {
        this.emailLinkLoading = false;
        this.linkClicked = true;
      });

    this.updateExpirationFailedSub = this.actions$
      .pipe(ofType(updatePDFExpirationFailed))
      .subscribe((action: any) => {
        this.emailLinkLoading = false;
        this.emailLinkError = action?.payload?.errorMessage || 'Request failed.';
      });

    this.pdf$ = this.store.select((store) => store.pdf);
    this.pdfSub = this.pdf$.subscribe((pdf) => {
      // If user has requested a new link, do not show or update the PDF viewer
      if (this.linkClicked) {
        this.isLoading = false;
        this.pdf = null;
        this.src = null;
        return;
      }

      this.isLoading = true;
      console.log('pdf', pdf)
      if (pdf) {
        if (pdf.status && pdf.status === 'OK') {
          this.pdf = pdf;
          this.isLoading = false
          console.log('pdf is not null')
          this.src = pdf.data.pdfRaw ? this.sanitizer.bypassSecurityTrustResourceUrl("data:application/pdf;base64," + pdf.data.pdfRaw) : null;
          console.log('src', this.src)
          this.orderLineItemId = null; // Clear when PDF loads successfully
        } else {
          this.pdf = null
          this.isLoading = false
          // Store orderLineItemId if provided in error response for renewal
          if (pdf.data && pdf.data.orderLineItemId) {
            this.orderLineItemId = pdf.data.orderLineItemId;
          }
        }
      }
    })
  }

  ngOnDestroy(): void {
    if (this.pdfSub) this.pdfSub.unsubscribe();
    if (this.updateExpirationSub) this.updateExpirationSub.unsubscribe();
    if (this.updateExpirationFailedSub) this.updateExpirationFailedSub.unsubscribe();
  }

  downloadPDF() {
    const pdfRaw = this.pdf?.data?.pdfRaw;
    if (!pdfRaw) return;
    const downloadLink = document.createElement("a");
    downloadLink.href = 'data:application/pdf;base64,' + pdfRaw;
    downloadLink.download = "Coverage Agreement.pdf";
    downloadLink.click();
  }

  updateLinkExpiration() {
    this.emailLinkError = null;
    this.emailLinkLoading = true;
    const uuid = this.route.snapshot.params['uuid'];
    
    // If we have orderLineItemId from expired link, use new renewal endpoint
    if (this.orderLineItemId) {
      this.dataLookup.requestContractLinkRenewal(this.orderLineItemId, brandInfo.brand).subscribe({
        next: (res: any) => {
          this.emailLinkLoading = false;
          if (res && res.status === 'OK') {
            this.linkClicked = true;
          } else {
            this.emailLinkError = res?.errorMessage || 'Failed to send renewal link';
          }
        },
        error: (err: any) => {
          this.emailLinkLoading = false;
          this.emailLinkError = err?.error?.errorMessage || 'Request failed. Please try again.';
        }
      });
    } else {
      // Fallback to old flow if orderLineItemId not available
      const request: any = {
        brandName: brandInfo.brand,
        uuid
      };
      this.store.dispatch(updatePDFExpiration(request));
    }
  }

}
